import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, Navigate, useNavigate, useParams} from '@tanstack/react-router'
import {Calendar, CheckCircle2, Clock, ExternalLink, Hourglass, Info, LogIn, Mail, MapPin, Medal, MessageCircle, Share2, Trophy, UserPlus, Users, X} from 'lucide-react'
import {useEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {formatRegistrationDate, signUpResultMessage} from '../components/events/registrationState.tsx'

import {config} from "@/config/config.ts";
import AdRails from "@/components/AdRails.tsx";
import {declarePageAds} from "@/ads/pageAds.ts";
import EventAuthModal, {PENDING_ACTION_KEY} from "../components/EventAuthModal.tsx";
import FinishPlayerProfileModal, {needsPlayerProfile} from "../components/registration/FinishPlayerProfileModal.tsx";
import JoinTournamentModal from "../components/JoinTournamentModal.tsx";
import EventCountdown from "../components/EventCountdown.tsx";
import LandingNav from "../landing/components/LandingNav.tsx";
import ResultsSection from "../results/ResultsSection.tsx";
import {shareResultsOn} from "../sports/registry.ts";
import StarIcon from "../landing/icons/StarIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";
import {useJwtUser} from "../JWTAuthProvider.tsx";
import {GradeToText, SportTypes} from "../constants.ts";
import {SPORT_AREAS} from "../sports/sportAreas.ts";
import services from "../services";
import {FieldLeaderboardSection} from "../fieldRounds/FieldLeaderboardPublic";

const APP_STATUS_LABEL: Record<number, string> = {
  2: "You're registered",
  6: 'Registration pending approval',
  8: "You're on the waiting list",
}

export const Route = createFileRoute('/e/$uid')({
  component: RouteComponent,
})

type PhaseUi = {
  label: string
  sub: string
  bg: string
  icon: typeof Clock
  cta?: string
}

const PHASE_UI: Record<string, PhaseUi> = {
  register:         {label: 'Registration Open', sub: 'Spots available, register to secure your place', bg: '#14a653', icon: CheckCircle2, cta: 'Register'},
  // Leagues only: play has started but the registration window is still open — keep the door open.
  register_live:    {label: 'League Started', sub: 'Play is underway — you can still register', bg: '#14a653', icon: CheckCircle2, cta: 'Register Now'},
  full:             {label: 'Tournament Full', sub: 'Join the waiting list to be next in line', bg: '#d9822b', icon: Users, cta: 'Join Waiting List'},
  generating_draws: {label: 'Generating Draws', sub: 'Draws are being prepared, starting soon', bg: '#2563eb', icon: Hourglass},
  happening:        {label: 'Happening Now', sub: 'The event is live', bg: '#14a653', icon: Clock},
  finished:         {label: 'Completed', sub: 'This event has finished', bg: '#475569', icon: CheckCircle2},
  not_open:         {label: 'Registration Opens Soon', sub: 'Check back to register', bg: '#475569', icon: Clock},
  closed:           {label: 'Registration Closed', sub: 'Registration is no longer open', bg: '#475569', icon: Clock},
}

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`
}

function fmtDateLong(iso?: string | null) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', {weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'})
}

function fmtDateModal(iso?: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-US', {weekday: 'short'})} ${ordinal(d.getDate())}, ${d.toLocaleDateString('en-US', {month: 'short'})}`
}

function fmtTime(iso?: string | null) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})
}

function RouteComponent() {
  const {uid} = useParams({from: '/e/$uid'})
  return <EventPageView uid={uid} />
}

/**
 * The public event/registration page. Used standalone at /e/$uid, and embedded (without the
 * public nav/footer) inside the organizer's tournament Home tab via `embedded`.
 */
export function EventPageView({uid, embedded = false}: {uid: string; embedded?: boolean}) {
  const navigate = useNavigate()
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [authAction, setAuthAction] = useState<null | 'register' | 'partner-join'>(null)
  // A new Google account comes back bare (EmptyAfterSignup) and no registration takes it: the
  // minimal profile first, then the action it was on its way to.
  const [finishProfileFor, setFinishProfileFor] = useState<null | 'register' | 'partner-join'>(null)
  const profileFinished = useRef(false)
  const [joinOpen, setJoinOpen] = useState(false)
  const user = useJwtUser()
  const queryClient = useQueryClient()

  const eventQuery = useQuery({
    // A /build/ event fills as players join: re-read it every half minute so names appear.
    refetchInterval: (q: any) => (q?.state?.data as any)?.live_entries ? 30_000 : false,
    queryKey: ['public-event', uid],
    queryFn:  () => services.publicEvent.show(uid),
    enabled:  !!uid,
  })

  // A link shared before the event was renamed: the server found it by its old slug. Move to the
  // current one, so the address bar (and anything copied from it) is the link that works.
  useEffect(() => {
    const ev: any = eventQuery.data
    if (ev?.slug && uid !== ev.slug && uid !== ev.uid) {
      navigate({to: '/e/$uid', params: {uid: ev.slug}, replace: true} as any)
    }
  }, [eventQuery.data, uid]) // eslint-disable-line react-hooks/exhaustive-deps

  const applyMutation = useMutation({
    mutationFn: () => {
      const ev = eventQuery.data
      return services.publicEvent.apply(ev.type === 'league' ? {league_uid: ev.uid} : ev.type === 'race' ? {race_uid: ev.uid} : {tournament_uid: ev.uid})
    },
    onSuccess: (res) => {
      addToast({color: 'success', title: res.waitlisted ? 'Added to the waiting list' : signUpResultMessage(res, !!res.pay_on_the_day)})
      queryClient.invalidateQueries({queryKey: ['public-event', uid]})
    },
    onError: (err: any) => addToast({color: 'danger', title: err?.message || 'Could not register'}),
  })

  const partnerMutation = useMutation({
    mutationFn: async (action: 'join' | 'leave') => {
      const ev = eventQuery.data
      const target = ev.type === 'league' ? {league_uid: ev.uid} : ev.type === 'race' ? {race_uid: ev.uid} : {tournament_uid: ev.uid}
      if (action === 'join') {
        await services.publicEvent.joinPartnerSearch(target)
      } else {
        await services.publicEvent.leavePartnerSearch(target)
      }
    },
    onSuccess: () => queryClient.invalidateQueries({queryKey: ['public-event', uid]}),
    onError: (err: any) => addToast({color: 'danger', title: err?.message || 'Something went wrong'}),
  })

  useEffect(() => {
    if (!user || !eventQuery.data) return
    const raw = sessionStorage.getItem(PENDING_ACTION_KEY)
    if (!raw) return
    let pending: {uid?: string; action?: string}
    try {
      pending = JSON.parse(raw)
    } catch {
      sessionStorage.removeItem(PENDING_ACTION_KEY)
      return
    }
    if (pending.uid !== uid) return
    sessionStorage.removeItem(PENDING_ACTION_KEY)
    // The same door as the button: a paid event goes to the register page's Checkout, not the quick modal.
    if (pending.action === 'register') startRegister()
    else if (pending.action === 'partner-join') joinPartnerList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, eventQuery.data])

  // A Plus club's registration page is ad-free: the ad script waits for this answer (ads/pageAds.ts).
  const hideAds = (eventQuery.data as any)?.hide_ads as boolean | undefined
  useEffect(() => {
    if (hideAds !== undefined) declarePageAds(!hideAds)
  }, [hideAds])

  if (eventQuery.isLoading) {
    return (
      <main className={`min-h-screen bg-[#0a1530] ${embedded ? 'rounded-2xl overflow-hidden' : ''}`}>
        {!embedded && <LandingNav />}
        <div className="flex justify-center pt-24"><Spinner size="lg" color="white" /></div>
      </main>
    )
  }

  const e = eventQuery.data
  if (!e) {
    return (
      <main className={`min-h-screen bg-[#0a1530] text-white ${embedded ? 'rounded-2xl overflow-hidden' : ''}`}>
        {!embedded && <LandingNav />}
        <div className="mx-auto max-w-[860px] px-6 py-24 text-center">
          <Trophy className="mx-auto mb-4 text-white/40" size={48} />
          <h1 className="text-2xl font-black">Event not found</h1>
          <p className="mt-2 text-white/60">This tournament or league is not available.</p>
        </div>
        {!embedded && <FooterSection />}
      </main>
    )
  }

  // Once the event is under way, take visitors straight to the live tournament view.
  if (!embedded && e.type === 'tournament' && e.registration_phase === 'happening') {
    return <Navigate to="/live/tournaments/$tournament_uid/" params={{tournament_uid: e.slug || e.uid}} replace />
  }

  const phase = PHASE_UI[e.registration_phase] ?? PHASE_UI.closed
  // Not open yet: when it opens if the organiser has set a start, otherwise just that it isn't.
  const opensLabel = e.registration_phase === 'not_open'
    ? (e.entry_starts_at && new Date(e.entry_starts_at).getTime() > Date.now()
      ? `Registration opens ${formatRegistrationDate(e.entry_starts_at)}`
      : "Registration isn't open yet")
    : null
  const fee = e.entry_amount
    ? `${e.entry_currency_code || ''} ${(e.entry_amount / 100).toLocaleString()}`.trim() + (e.entry_fee_basis === 'per_player' ? ' / player' : '')
    : 'Free'
  const myStatus: number | undefined = e.my_application?.status
  const myStatusLabel = myStatus ? APP_STATUS_LABEL[myStatus] : undefined
  const isExternal = e.registration_channel === 'external' && !!e.registration_external_url
  const partnerSeekers: any[] = e.partner_seekers || []
  const registeredNames: string[] = e.registered || []
  // Segments for the "View all" panel: doubles entrants without a partner yet, the partner-seeker
  // pool, and entrants bumped to the waiting list once the participant threshold was crossed.
  const waitingForPartner: string[] = e.waiting_for_partner || []
  const lookingForPartner: string[] = partnerSeekers.map((p: any) => p.name).filter(Boolean)
  const waitlistedNames: string[] = e.waitlisted || []
  // Registration is over but the draw isn't out yet — hold visitors with a "waiting for draw" message.
  const waitingForDraw = e.type === 'tournament'
    && ['closed', 'generating_draws'].includes(e.registration_phase)
  // Event is live (embedded only — a live event redirects above) or finished — surface the live view.
  const showLiveView = e.type === 'tournament'
    && ['happening', 'finished'].includes(e.registration_phase)
  const location = [e.venue, e.city].filter(Boolean).join(', ')
  const sportLabel = e.sport_type
    ? ((SportTypes as Record<string, string>)[e.sport_type] || String(e.sport_type).charAt(0).toUpperCase() + String(e.sport_type).slice(1).replace(/_/g, ' '))
    : null
  const progress = e.max_participants ? Math.min(100, (e.approved_count / e.max_participants) * 100) : 0
  // Gated team sports (football/basketball/cricket/esports) register a whole team — no doubles/partner flow.
  const isTeamSportArea = !!(e.sport_type && (SPORT_AREAS as Record<string, any>)[e.sport_type])
  // Any team sport (the server's SportType::isTeamBased): entrants are named teams, never pairs.
  const teamSport = isTeamSportArea || !!e.team_sport
  const isTeams = Number(e.participants_type) === 2 || !!e.team_sport
  const countUnit = isTeams ? 'teams' : 'players'
  // Pairs: the teams the organizer formed against the people who registered. A divisions season's
  // entrants sign up alone and are paired per division later, so its players are not twice its
  // teams — and until anyone is paired they are players, not teams.
  const teamsCount = Number(e.teams_count || 0)
  const playersCount = Number(e.players_count || 0)
  const pairsEvent = isTeams && !teamSport
  const soloEntrants = pairsEvent && teamsCount === 0 && playersCount > 0 && playersCount <= (e.approved_count || 0)
  const headlineCount = pairsEvent && teamsCount > 0 ? teamsCount : soloEntrants ? playersCount : (e.approved_count || 0)
  const headlineUnit = soloEntrants ? 'players' : countUnit
  const headlinePlayers = pairsEvent && !soloEntrants ? (playersCount || headlineCount * 2) : 0
  // A league with INDIVIDUAL ranking (DoublesIndividual) is a solo entry: players sign up alone
  // and pairs form per game, so nobody is "waiting for a partner". Same rule the fee and
  // registration code use — on a TOURNAMENT that type still means doubles with a real partner.
  const isSoloEntry = e.type === 'league' && Number(e.participants_type) === 1

  // Setup → Participants, but only for the organizer looking at their own event. Races manage
  // entries in their own area rather than under /tournaments|/leagues, so they get no link.
  const isOrganizer = !!(user?.uid && e.organizer?.uid && user.uid === e.organizer.uid)
  const approvalsHref = isOrganizer && e.type !== 'race'
    ? `/${e.type === 'league' ? 'leagues' : 'tournaments'}/${e.uid}/setup/applications/`
    : null

  // Organizer branding (set in the event's Branding tab): the hero shows the logo and banner,
  // but keeps the default ScorePal colours — the brand background/text colours apply to the
  // live view only, so this page stays readable whatever palette the organizer picked.
  const brandLogo = (e.logo_thumb_url || e.logo_url) as string | null
  const brandBanner = e.registration_banner_url as string | null
  const canApply = phase.cta && !myStatusLabel
  // A doubles entrant who registered with "add partner later" can return to set their partner.
  // Solo-entry leagues excluded: entrants there have no partner to add, and the register page
  // would not know what to do with them.
  // An entry fee above zero: Checkout or pay on the day, never the quick modal's free entry.
  const feeDue = Number(e.entry_amount || 0) > 0
  const needsPartner = !!e.my_application && !e.my_application.partner_player_uid
    && !teamSport && Number(e.participants_type) !== 3 && !isSoloEntry

  const mustFinishProfile = () => needsPlayerProfile(user) && !profileFinished.current

  const joinPartnerList = () => {
    if (mustFinishProfile()) {
      setFinishProfileFor('partner-join')
      return
    }
    partnerMutation.mutate('join')
  }

  const startRegister = () => {
    if (mustFinishProfile()) {
      setFinishProfileFor('register')
      return
    }
    // Tournaments and leagues use the registration page (handles divisions when configured, or a
    // single whole-event entry when not). Gated team sports keep the dedicated team-registration modal
    // for FREE events; an entry fee is taken by the register page's Checkout step, so a signed-in
    // player on a paid event goes there whatever the sport. (A guest on a paid team-sport event
    // still uses the modal: it opens Checkout once the team is entered.)
    if (!isTeamSportArea || (user && feeDue)) {
      navigate({to: '/e/$uid/register/', params: {uid}})
      return
    }
    setJoinOpen(true)
  }

  const handleRegister = () => {
    if (isExternal) {
      window.open(e.registration_external_url, '_blank', 'noopener')
      return
    }
    if (!user) {
      // Team sports register without a login — open the guest team-registration modal directly.
      if (isTeamSportArea) {
        startRegister()
        return
      }
      setAuthAction('register')
      return
    }
    startRegister()
  }

  const onAuthenticated = () => {
    const action = authAction
    setAuthAction(null)
    if (action === 'register') startRegister()
    else if (action === 'partner-join') joinPartnerList()
  }

  const shareEvent = async () => {
    const url = window.location.href
    if (navigator.share) {
      try { await navigator.share({title: e.title, url}) } catch { /* dismissed */ }
    } else {
      await navigator.clipboard.writeText(url)
      addToast({color: 'success', title: 'Link copied'})
    }
  }

  const openMaps = () => {
    if (!location) return
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`, '_blank', 'noopener')
  }

  const contactOrganizer = () => {
    if (isExternal && e.registration_external_url) {
      window.open(e.registration_external_url, '_blank', 'noopener')
    } else if (e.organizer?.email) {
      window.location.href = `mailto:${e.organizer.email}`
    }
  }

  const partnerToggle = () => {
    if (!user) { setAuthAction('partner-join'); return }
    if (e.my_partner_seeker) partnerMutation.mutate('leave')
    else joinPartnerList()
  }

  // Per-player: the viewer is a partner whose own entry fee is still due.
  const partnerPaymentDue = e.my_partner_payment_due
  const payPartnerEntry = async () => {
    if (!partnerPaymentDue) return
    try {
      const res = await services.publicEvent.payPartnerEntry(partnerPaymentDue.application_uid)
      if (res.error || !res.redirect_url) { addToast({color: 'danger', title: res.error || 'Could not start checkout. Please try again.'}); return }
      document.location.href = res.redirect_url
    } catch (err: any) {
      addToast({color: 'danger', title: err?.message || 'Could not start checkout.'})
    }
  }

  return (
    <main className={`min-h-screen bg-[#06133e] font-inter text-white ${embedded ? 'rounded-2xl overflow-hidden' : ''}`}>
      {!embedded && <LandingNav />}

      {/* Fixed advertisement rails in the page gutters — only mounted on wide screens (>=1850px),
          so they never overlap the centred content or steal the content banner's ad push. */}
      {/* no_ads: hidden when the OWNING club's plan includes ad-free pages (hide_ads from the
          event payload, decided server-side). */}
      {!embedded && !(e as any)?.hide_ads && <AdRails slot={config.adsenseRegistrationSlot} />}

      {e.starts_at && new Date(e.starts_at).getTime() > Date.now() ? (
        <div className="container mx-auto px-4 pt-4 lg:px-12">
          <div className="rounded-2xl border border-white/10 bg-white/95 py-2.5 text-center text-sm font-semibold text-[#1a2752]">
            <CheckCircle2 size={14} className="-mt-1 mr-1 inline text-[#14a653]" />{' '}
            <EventCountdown to={e.starts_at} prefix={`${e.type === 'league' ? 'League' : e.type === 'race' ? 'Race' : 'Tournament'} starts in`} />
          </div>
        </div>
      ) : null}

      <div className="container @container mx-auto px-4 pt-6 lg:px-12">
        {/* Hero (full width) */}
        <section
          className="relative overflow-hidden rounded-3xl p-10 text-center shadow-[0_18px_45px_rgba(8,47,138,0.18)] bg-gradient-to-br from-[#06133e] via-[#0c2c7d] to-[#0a1530] text-white"
        >
            {brandBanner ? (
              <>
                <img src={brandBanner} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/65" />
              </>
            ) : null}
            <div className="relative z-10">
            {brandLogo ? (
              <img src={brandLogo} alt={e.title} className="mx-auto mb-4 h-16 w-auto max-w-[200px] rounded-2xl object-contain" />
            ) : null}
            <div className="mx-auto mb-5 flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-bold backdrop-blur">
              <StarIcon size={12} /> Powered by ScorePal
            </div>
            <h1 className="font-bebas text-[clamp(34px,5vw,52px)] font-bold uppercase leading-[0.95] tracking-wide">{e.title}</h1>
            {/* One sponsor slot (logo, link): the first sponsor set on the event. */}
            {(e as any).sponsor?.image_url ? (
              <a href={(e as any).sponsor.link_url || undefined} target="_blank" rel="noopener noreferrer sponsored"
                 className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 backdrop-blur">
                <span className="text-[10px] font-bold uppercase tracking-wide text-white/60">Sponsored by</span>
                <img src={(e as any).sponsor.thumb_url || (e as any).sponsor.image_url} alt="Sponsor" className="h-6 w-auto max-w-[120px] object-contain" />
              </a>
            ) : null}
            <p className="mx-auto mt-3 max-w-xl text-sm font-medium text-white/75">
              Live {e.type === 'league' ? 'league' : e.type === 'race' ? 'race' : 'tournament'} details, match status, venue information, and organizer actions in one.
            </p>

            <div className="mt-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-white/60">Entry Fee</div>
              <div className="font-bebas text-4xl font-bold leading-tight">{fee}</div>
              {e.entry_amount ? <div className="text-xs font-medium text-white/60">per player</div> : null}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <HeroBadge dark>ScorePal Verified</HeroBadge>
              {sportLabel ? <HeroBadge>{sportLabel}</HeroBadge> : null}
              {e.format ? <HeroBadge>{e.format}</HeroBadge> : null}
              <HeroBadge>Scores Recorded</HeroBadge>
              {e.grade_default ? <HeroBadge>{GradeToText(e.grade_default, e.sport_type)}</HeroBadge> : null}
              {e.dupr_premium_only ? (
                <span title="Only DUPR Premium (DUPR+) members can register"
                      className="rounded-full bg-[#F2E02D] px-3 py-1 text-xs font-black uppercase tracking-wide text-[#06133e]">
                  ★ DUPR+ Members Only
                </span>
              ) : null}
            </div>
            </div>
        </section>

        {/* A finished event opens on its results: the champion, the podium, the road to victory. */}
        {shareResultsOn() && e.registration_phase === 'finished' && <ResultsSection eventUid={e.uid} />}

        <div className="mt-5 grid gap-5 @5xl:grid-cols-[minmax(0,1fr)_280px_320px]">
          <div className="flex min-w-0 flex-col gap-5">
          <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-2 backdrop-blur">
            {/* Private (1) and Public (3) events are both reachable by link — only ForcedPrivate (4) hides registration. */}
            {e.visibility_type === 4 ? (
              <div className="p-4">
                <h2 className="text-lg font-black text-white">Registration Disabled</h2>
                <p className="mt-1 text-sm text-white/65">
                  The organizer has disabled the public registration page for this event. Please contact the organizer for details on how to join.
                </p>
              </div>
            ) : (
            <div className="grid items-stretch gap-2 sm:grid-cols-[1fr_auto]">
              <div className="p-4">
                <h2 className="text-lg font-black text-white">{myStatusLabel || opensLabel || (waitingForDraw ? 'Registration Closed' : showLiveView || e.registration_phase === 'register_live' ? phase.label : e.requires_approval ? 'Request to Join' : 'Join this event')}</h2>
                <p className="mt-1 text-sm text-white/65">
                  {opensLabel && !myStatusLabel ? (
                    e.entry_starts_at && new Date(e.entry_starts_at).getTime() > Date.now() ? 'Check back then to register' : "The organiser hasn't opened sign-ups yet"
                  ) : e.entry_ends_at && new Date(e.entry_ends_at).getTime() > Date.now() ? (
                    <>
                      <EventCountdown to={e.entry_ends_at} prefix="Registration closes in" />
                      {myStatusLabel ? null : <> &middot; {e.requires_approval ? 'Requires organizer approval' : 'Your place is confirmed when you sign up'}</>}
                    </>
                  ) : (
                    myStatusLabel ? phase.sub
                      : waitingForDraw ? 'Waiting for draw'
                      : showLiveView ? 'Keep track of scores and results — open the live tournament view'
                      : e.requires_approval ? 'Requires organizer approval' : 'Your place is confirmed when you sign up'
                  )}
                </p>
              </div>
              {partnerPaymentDue ? (
                <button
                  type="button"
                  onClick={payPartnerEntry}
                  className="flex min-w-[120px] flex-col items-center justify-center rounded-2xl border border-[#EADB2C]/50 bg-[#EADB2C]/15 px-6 py-3 transition hover:bg-[#EADB2C]/25"
                >
                  <CheckCircle2 size={22} className="mb-1 text-[#EADB2C]" />
                  <span className="text-xs font-bold text-[#EADB2C]">Pay your entry fee</span>
                </button>
              ) : myStatusLabel ? (
                <div className="flex flex-col items-stretch gap-2">
                  <div className="flex min-w-[120px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-3">
                    <CheckCircle2 size={22} className="mb-1 text-[#14a653]" />
                    <span className="text-xs font-bold text-white">{myStatusLabel}</span>
                  </div>
                  {needsPartner && (
                    <button
                      type="button"
                      onClick={startRegister}
                      className="flex min-w-[120px] items-center justify-center gap-1.5 rounded-2xl border border-[#7ba6ff]/40 bg-[#7ba6ff]/10 px-4 py-2 text-xs font-bold text-[#7ba6ff] transition hover:bg-[#7ba6ff]/20"
                    >
                      <UserPlus size={16} /> Add partner
                    </button>
                  )}
                </div>
              ) : canApply ? (
                <button
                  type="button"
                  onClick={handleRegister}
                  disabled={applyMutation.isPending}
                  className="cta-spin-border flex min-w-[120px] flex-col items-center justify-center rounded-2xl border border-white/25 bg-white/[0.08] px-6 py-3 transition hover:bg-white/15 disabled:opacity-70"
                >
                  {isExternal ? <ExternalLink size={22} className="mb-1 text-[#7ba6ff]" />
                    : !user ? <LogIn size={22} className="mb-1 text-[#7ba6ff]" />
                    : <CheckCircle2 size={22} className="mb-1 text-[#7ba6ff]" />}
                  <span className="text-xs font-bold text-[#7ba6ff]">
                    {applyMutation.isPending ? 'Submitting...' : !user && !isExternal && e.registration_phase !== 'register_live' ? 'Register' : phase.cta}
                  </span>
                </button>
              ) : showLiveView ? (
                <a
                  href={`/live/tournaments/${e.uid}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex min-w-[120px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-3 transition hover:bg-white/10"
                >
                  <Trophy size={22} className="mb-1 text-[#7ba6ff]" />
                  <span className="text-xs font-bold text-[#7ba6ff]">Live Tournament View</span>
                </a>
              ) : waitingForDraw ? (
                <div className="flex min-w-[120px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-3">
                  <Hourglass size={22} className="mb-1 text-[#7ba6ff]" />
                  <span className="text-xs font-bold text-white/65">Waiting for draw</span>
                </div>
              ) : (
                <div className="flex min-w-[120px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] px-6 py-3">
                  <span className="text-xs font-bold text-white/65">{opensLabel ?? phase.label}</span>
                </div>
              )}
            </div>
            )}
          </section>

          <section className="flex flex-1 flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-black text-white">{isSoloEntry ? 'Players' : 'Team'}</h2>
              {/* Solo-entry leagues have no partners to find — everyone enters alone and pairs
                  form per game, so the partner-seeker pool would be an empty promise. */}
              {/* A team sport registers a whole team: there is no partner to find. */}
              {!isSoloEntry && !teamSport && (
                <button
                  onClick={partnerToggle}
                  disabled={partnerMutation.isPending}
                  className="text-sm font-bold text-white underline-offset-4 transition hover:underline disabled:opacity-70"
                >
                  {!user ? 'Log in to find partner' : e.my_partner_seeker ? 'Stop looking' : 'Find a Partner'}
                </button>
              )}
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-baseline gap-2 text-white">
                  <Users size={18} className="self-center text-white/65" />
                  <span className="text-2xl font-black"><CountUp value={headlineCount} /></span>
                  <span className="text-sm font-semibold text-white/65">{headlineUnit}{e.max_participants ? ` / ${e.max_participants}` : ''}</span>
                  {/* The people behind the pairs — team-sport squads (football etc.) have arbitrary roster sizes, so none there. */}
                  {headlinePlayers > 0 ? <span className="text-xs text-white/50">({headlinePlayers} players)</span> : null}
                  {(e.pending_count || 0) > 0 ? (
                    // Organizers repeatedly tapped this chip expecting to reach the approvals list
                    // (rage clicks logged on the public event page), so for them it is a real link
                    // to Setup → Participants. Everyone else still sees a plain informational chip.
                    approvalsHref ? (
                      <a
                        href={approvalsHref}
                        title="Review players awaiting approval"
                        className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2.5 py-0.5 text-xs font-bold text-amber-300 underline-offset-2 transition hover:bg-amber-400/25 hover:underline"
                      >
                        +{e.pending_count} awaiting approval
                      </a>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-amber-400/15 px-2.5 py-0.5 text-xs font-bold text-amber-300">
                        +{e.pending_count} awaiting approval
                      </span>
                    )
                  ) : null}
                </div>
                {partnerSeekers.length > 0 && !isSoloEntry ? (
                  <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white">
                    {partnerSeekers.length} looking for partner
                  </span>
                ) : null}
              </div>
              {e.max_participants ? (
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#3b6bdc] to-[#5a8aff]" style={{width: `${progress}%`}} />
                </div>
              ) : null}
              {(registeredNames.length > 0 || waitingForPartner.length > 0 || lookingForPartner.length > 0 || waitlistedNames.length > 0)
                ? <RegisteredMarquee names={registeredNames} unit={headlineUnit}
                                     segments={{waitingForPartner, lookingForPartner: isSoloEntry ? [] : lookingForPartner, waitlisted: waitlistedNames}}
                                     divisions={e.divisions_with_teams || []} soloEntry={isSoloEntry} />
                : null}
            </div>
          </section>

          {/* Lineup Coming Soon block removed per Shaun's request — empty-state is */}
          {/* covered by the Team count card above. */}
        </div>

        {/* Middle column of the 3-column row: Date / Time / Location / Level. */}
        <section className="flex flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
          {sportLabel ? (
            <SidebarRow icon={<Medal size={18} />} label="Sport" value={sportLabel} />
          ) : null}
          {e.format ? (
            <SidebarRow icon={<Trophy size={18} />} label="Format" value={e.format} />
          ) : null}
          <SidebarRow icon={<Calendar size={18} />} label="Date" value={fmtDateLong(e.starts_at) || 'TBC'} />
          <SidebarRow icon={<Clock size={18} />} label="Time" value={fmtTime(e.starts_at) || 'TBC'} />
          <SidebarRow
            icon={<MapPin size={18} />}
            label="Location"
            value={e.venue || e.city || 'TBC'}
            extra={e.venue && e.city ? e.city : undefined}
            action={location ? <button onClick={openMaps} className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-[#7ba6ff] hover:underline">Get Direction <ExternalLink size={12} /></button> : undefined}
          />
          {e.grade_default ? (
            <SidebarRow icon={<Trophy size={18} />} label="Level" value={GradeToText(e.grade_default, e.sport_type)} last />
          ) : null}
        </section>

        <aside className="flex flex-col gap-4">
          <div className="flex flex-1 flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur">
            <div className="mb-3 flex items-center gap-2 text-sm font-black text-white">
              <span className="grid h-6 w-6 place-items-center rounded-md bg-white/10 text-[#7ba6ff]">
                <CheckCircle2 size={14} />
              </span>
              Organized By
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
              <div className="flex items-start gap-3">
                {e.organizer?.image ? (
                  <img src={e.organizer.image} alt="" className="h-9 w-9 shrink-0 rounded-lg border border-white/15 object-cover" />
                ) : (
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/10 text-xs font-black text-[#7ba6ff]">
                    {((e.organizer?.name || 'S') as string).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-white">{e.organizer?.name || 'ScorePal Events'}</div>
                  <p className="mt-0.5 text-xs leading-relaxed text-white/65">Managed with ScorePal for registrations, match tracking, and live updates.</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setDetailsOpen(true)}
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#082f8a] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#06266f]"
            >
              <Info size={16} /> View All Details
            </button>
            {(isExternal || e.organizer?.email) ? (
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={contactOrganizer}
                  className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/20 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-white/5"
                >
                  <MessageCircle size={16} /> Contact Organizer
                </button>
                <button
                  onClick={shareEvent}
                  aria-label="Share"
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/20 text-white transition hover:bg-white/5"
                >
                  <Share2 size={16} />
                </button>
              </div>
            ) : null}
          </div>

          </aside>
        </div>

        {/* Golf and ten-pin with multi-sport on (Prompt 8): the leaderboard once a round starts. */}
        {e.type === 'tournament' && <FieldLeaderboardSection tournamentUid={e.uid} sport={e.sport_type} />}
      </div>

      <DetailsDrawer open={detailsOpen} onClose={() => setDetailsOpen(false)} event={e} fee={fee} />

      <FinishPlayerProfileModal
        open={finishProfileFor !== null}
        sport={e.sport_type}
        onClose={() => setFinishProfileFor(null)}
        onDone={() => {
          profileFinished.current = true
          const next = finishProfileFor
          setFinishProfileFor(null)
          if (next === 'register') startRegister()
          else if (next === 'partner-join') partnerMutation.mutate('join')
        }}
      />

      <EventAuthModal
        open={authAction !== null}
        onClose={() => setAuthAction(null)}
        onAuthenticated={onAuthenticated}
        eventTitle={e.title}
        uid={uid}
        action={authAction || 'register'}
      />

      <JoinTournamentModal
        open={joinOpen}
        onClose={() => setJoinOpen(false)}
        onJoined={() => {
          setJoinOpen(false)
          queryClient.invalidateQueries({queryKey: ['public-event', uid]})
        }}
        eventUid={e.uid}
        eventType={e.type}
        participantsType={e.participants_type}
        isTeamSport={isTeamSportArea}
        isGuest={!user}
        viewerName={user?.display_name || user?.name}
        requiresPayment={feeDue}
        paymentsReady={!!e.payments_ready}
        divisions={e.divisions}
        registrationFields={e.registration_fields}
        waivers={e.waivers}
      />

      <div className="[&_section]:!bg-[#06133e]">
        {!embedded && <FooterSection />}
      </div>
    </main>
  )
}

function DetailsDrawer({open, onClose, event, fee}: {open: boolean; onClose: () => void; event: any; fee: string}) {
  if (!open) return null
  const poster = event.public_card_image_url || event.banner_url
  const isToday = event.starts_at && new Date(event.starts_at).toDateString() === new Date().toDateString()
  const prizeLines: string[] = (event.prize || '').split(/\r?\n/).map((s: string) => s.trim()).filter(Boolean)
  const prizeColors = ['#f5b041', '#cbd5e1', '#cd7f32']
  const prizeLabels = ['1st Place', '2nd Place', '3rd Place']
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/75">
      <button aria-label="Close" className="absolute inset-0 cursor-default" onClick={onClose} />
      <aside className="relative h-full w-full max-w-[480px] overflow-y-auto bg-[#06133e] shadow-2xl">
        <button
          aria-label="Close details"
          onClick={onClose}
          className="absolute right-4 top-3 z-20 grid h-11 w-11 place-items-center rounded-full bg-[#071a5f]/35 text-white backdrop-blur transition hover:bg-[#071a5f]"
        >
          <X size={22} />
        </button>

        {poster ? (
          <img src={poster} alt={event.title} className="aspect-[4/5] w-full object-cover" />
        ) : (
          <div className="grid aspect-[4/5] w-full place-items-center bg-gradient-to-br from-[#06133e] via-[#0d48b5] to-[#03102b] text-white">
            <Trophy size={76} />
          </div>
        )}

        <section className="p-6">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
              <StarIcon className="text-white" size={12} /> {isToday ? 'Today' : 'Upcoming'}
            </span>
            {event.grade_default ? (
              <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white backdrop-blur">
                {GradeToText(event.grade_default, event.sport_type)}
              </span>
            ) : null}
          </div>

          <h2 className="font-bebas text-3xl font-bold uppercase leading-tight tracking-wide text-white">{event.title}</h2>
          <p className="mt-3 text-sm font-medium text-white/70">{fmtDateModal(event.starts_at)} • {fmtTime(event.starts_at)}</p>

          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-bebas text-3xl font-bold text-white">{fee}</span>
            {event.entry_amount ? <span className="text-sm text-white/65">per player</span> : null}
          </div>

          {prizeLines.length > 0 ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <h3 className="mb-3 text-base font-black text-white">Prizes &amp; Awards</h3>
              <div className="space-y-2">
                {prizeLines.map((line, i) => {
                  const color = prizeColors[i] || '#7ba6ff'
                  const parts = line.split(/[:—-]\s*/)
                  const title = parts.length > 1 ? parts[0] : (prizeLabels[i] || `Prize ${i + 1}`)
                  const desc = parts.length > 1 ? parts.slice(1).join(': ') : line
                  return (
                    <div key={i} className="flex items-start gap-3 rounded-xl bg-white/[0.04] p-3">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white" style={{backgroundColor: color}}>
                        <Medal size={16} />
                      </span>
                      <div className="min-w-0">
                        <div className="text-sm font-black text-white">{title}</div>
                        <div className="truncate text-xs text-white/65">{desc}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : null}

          {event.registration_about ? (
            <div className="mt-6">
              <h3 className="mb-2 text-base font-black text-white">About</h3>
              <p className="whitespace-pre-line text-sm leading-relaxed text-white/80">{event.registration_about}</p>
            </div>
          ) : null}

          {(event.venue || event.city || event.organizer) ? (
            <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.04] p-3">
              <div className="flex items-center gap-3">
                {event.organizer?.image ? (
                  <img src={event.organizer.image} alt="" className="h-10 w-10 rounded-xl border border-white/15 object-cover" />
                ) : (
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-white/10 text-sm font-black text-[#7ba6ff]">
                    {((event.organizer?.name || event.venue || '?') as string).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-black text-white">{event.organizer?.name || event.venue || 'Organizer'}</div>
                  {(event.venue || event.city) ? <div className="truncate text-xs text-white/65">{[event.venue, event.city].filter(Boolean).join(', ')}</div> : null}
                </div>
              </div>
            </div>
          ) : null}

          {event.organizer ? (
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-bold text-white">
                  <Info size={12} /> Organized By
                </div>
                <div className="truncate text-sm font-bold text-[#7ba6ff]">{event.organizer.name}</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-bold text-white">
                  <CheckCircle2 size={12} /> Submitted By
                </div>
                <div className="truncate text-sm font-bold text-[#7ba6ff]">{event.organizer.name}</div>
              </div>
            </div>
          ) : null}

          {event.organizer?.email ? (
            <a href={`mailto:${event.organizer.email}`} className="mt-4 flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white">
              <Mail size={14} className="text-[#7ba6ff]" /> {event.organizer.email}
            </a>
          ) : null}
        </section>
      </aside>
    </div>
  )
}

/** Animated count-up for the entrants number; jumps straight to the value for reduced-motion users. */
function CountUp({value}: {value: number}) {
  const [shown, setShown] = useState(0)

  useEffect(() => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setShown(value)
      return
    }
    let raf = 0
    const start = performance.now()
    const duration = 900
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setShown(Math.round(eased * value))
      if (t < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value])

  return <>{shown}</>
}

/** Continuously scrolling ribbon of registered team/player names. Pauses on hover; static wrap for reduced motion. */
function RegisteredMarquee({names, unit = 'teams', segments, divisions, soloEntry = false}: {
  names: string[];
  unit?: string;
  segments?: {waitingForPartner: string[]; lookingForPartner: string[]; waitlisted: string[]};
  divisions?: {key: string; name: string; count: number; teams: {name: string; players: string[]}[]}[];
  /** Entrants sign up alone and pair per game (individual-ranking league) — no partner to wait for. */
  soloEntry?: boolean;
}) {
  const [listOpen, setListOpen] = useState(false)
  const seg = segments || {waitingForPartner: [], lookingForPartner: [], waitlisted: []}
  // Division tournaments split the "who's in" list into one section per division.
  const divisionGroups = (divisions || []).filter((d) => d.teams && d.teams.length > 0)
  const divisionTotal = divisionGroups.reduce((sum, d) => sum + d.teams.length, 0)

  // One labelled segment in the slide-in panel (rendered only when it has entrants).
  const segmentList = (title: string, hint: string, items: string[]) => items.length > 0 && (
    <div className="mt-6">
      <h3 className="text-lg font-black text-white">{title} <span className="text-white/40">({items.length})</span></h3>
      <p className="text-xs font-semibold text-white/55">{hint}</p>
      <ul className="mt-3 flex flex-col gap-1.5">
        {items.map((n, i) => (
          <li key={i} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-xs font-black text-white">
              {n.charAt(0).toUpperCase()}
            </span>
            <span className="text-sm font-bold text-white/90">{n}</span>
          </li>
        ))}
      </ul>
    </div>
  )
  // Duplicate the list so the loop is seamless; slow the scroll down as the list grows.
  const duration = Math.max(18, names.length * 3)
  const chip = (name: string, i: number, dup: boolean) => (
    <span key={`${dup ? 'b' : 'a'}-${i}`} className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] py-1 pl-1 pr-3 text-xs font-bold text-white/85">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/10 text-[10px] font-black text-white">
        {name.charAt(0).toUpperCase()}
      </span>
      {name}
    </span>
  )

  return (
    <div className="mt-3">
      <style>{`
        @keyframes sp-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .sp-marquee-track { animation: sp-marquee ${duration}s linear infinite; width: max-content; }
        .sp-marquee:hover .sp-marquee-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .sp-marquee-track { animation: none; flex-wrap: wrap; width: auto; }
          .sp-marquee-dup { display: none; }
        }
      `}</style>
      {names.length > 0 && <>
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">Who's in</div>
        <div className="sp-marquee mt-1.5 overflow-hidden"
             style={{maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)'}}>
          <div className="sp-marquee-track flex gap-2">
            {names.map((n, i) => chip(n, i, false))}
            <span className="sp-marquee-dup flex gap-2">{names.map((n, i) => chip(n, i, true))}</span>
          </div>
        </div>
      </>}

      <button
        onClick={() => setListOpen(true)}
        className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#082f8a] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-[#06266f]"
      >
        <Users size={16} /> View All {unit === 'players' ? 'Players' : 'Teams'} ({names.length})
      </button>

      {listOpen ? createPortal(
        <div className="fixed inset-0 z-50 flex justify-start bg-black/75">
          <style>{`@keyframes sp-slide-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
          <button aria-label="Close" className="absolute inset-0 cursor-default" onClick={() => setListOpen(false)} />
          <aside className="relative h-full w-full max-w-[480px] overflow-y-auto bg-[#06133e] shadow-2xl"
                 style={{animation: 'sp-slide-in-left 0.28s ease-out'}}>
            <button
              aria-label="Close list"
              onClick={() => setListOpen(false)}
              className="absolute right-4 top-3 z-20 grid h-11 w-11 place-items-center rounded-full bg-[#071a5f]/35 text-white backdrop-blur transition hover:bg-[#071a5f]"
            >
              <X size={22} />
            </button>

            <section className="p-6">
              {divisionGroups.length > 0 ? <>
                <h3 className="text-lg font-black text-white">Who's in</h3>
                <p className="text-xs font-semibold text-white/55">{divisionTotal} {divisionTotal === 1 ? (unit === 'players' ? 'player' : 'team') : unit} across {divisionGroups.length} division{divisionGroups.length > 1 ? 's' : ''}</p>
                {divisionGroups.map((d) => (
                  <div key={d.key} className="mt-5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{d.name} <span className="text-white/30">({d.teams.length})</span></div>
                    <ul className="mt-2 flex flex-col gap-1.5">
                      {d.teams.map((t, i) => (
                        <li key={i} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-xs font-black text-white">
                            {(t.name || '?').charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-bold text-white/90">{t.name}</span>
                            {t.players.length > 0 ? <span className="block truncate text-xs font-semibold text-white/45">{t.players.join(' & ')}</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </> : names.length > 0 && <>
                <h3 className="text-lg font-black text-white">Who's in</h3>
                <p className="text-xs font-semibold text-white/55">{names.length} {unit} registered</p>
                <ul className="mt-4 flex flex-col gap-1.5">
                  {names.map((n, i) => (
                    <li key={i} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2">
                      <span className="w-6 text-right text-xs font-bold tabular-nums text-white/40">{i + 1}</span>
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10 text-xs font-black text-white">
                        {n.charAt(0).toUpperCase()}
                      </span>
                      <span className="text-sm font-bold text-white/90">{n}</span>
                    </li>
                  ))}
                </ul>
              </>}

              {soloEntry
                ? segmentList('Entered', 'Awaiting approval', seg.waitingForPartner)
                : segmentList('Waiting for Partner', 'Entered without a partner', seg.waitingForPartner)}
              {segmentList('Looking for a Partner', 'Open to teaming up', seg.lookingForPartner)}
              {segmentList('Waiting list', 'Joins once a spot opens up', seg.waitlisted)}
            </section>
          </aside>
        </div>,
        document.body,
      ) : null}
    </div>
  )
}

function HeroBadge({children, dark = false}: {children: React.ReactNode; dark?: boolean}) {
  return (
    <span className={dark
      ? 'rounded-full bg-white px-3 py-1.5 text-[11px] font-bold text-[#06195f]'
      : 'rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-[11px] font-bold text-white backdrop-blur'}>
      {children}
    </span>
  )
}

function SidebarRow({icon, label, value, extra, action, last = false}: {
  icon: React.ReactNode; label: string; value: string; extra?: string; action?: React.ReactNode; last?: boolean
}) {
  return (
    <div className={`flex items-start gap-3 ${last ? '' : 'mb-3 pb-3'}`}>
      <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/10 text-[#7ba6ff]">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-white/55">{label}</div>
        <div className="truncate text-sm font-bold text-white">{value}</div>
        {extra ? <div className="truncate text-xs text-white/65">{extra}</div> : null}
        {action || null}
      </div>
    </div>
  )
}
